// The only module that calls the ops device routes (ADR-0029, ops-api.md 1). The console never holds an
// ops token: it only approves or denies a device flow with the operator's Firebase ID token.
import { ApiError } from "./client";
import type { ErrorBody } from "./types";

/** What the approval page shows before the operator decides (Q19 stopgap, `POST /v1/ops/device/lookup`). */
export interface OpsDeviceInfo {
  env: string;
  scope: string;
  device_ip: string | null;
  expires_in: number;
}

/** The result of an approval or a deny. */
export interface OpsDeviceDecision {
  ok: boolean;
  env: string;
  scope: string;
}

export interface OpsClientOptions {
  /** Base URL of the STS host. */
  baseUrl: string;
  /** Returns a current Firebase ID token. */
  getIdToken: () => Promise<string>;
  fetch?: typeof fetch;
}

/** Typed client for the console part of the ops device flow. */
export function createOpsDeviceClient(opts: OpsClientOptions) {
  const base = opts.baseUrl.replace(/\/+$/, "");
  const doFetch = opts.fetch ?? globalThis.fetch.bind(globalThis);

  async function post<T>(path: string, body: unknown): Promise<T> {
    const token = await opts.getIdToken();
    let res: Response;
    try {
      res = await doFetch(`${base}/v1/ops/device${path}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
    } catch {
      throw new ApiError(0, "network", "Cannot reach the Prego API. Check your connection.", null);
    }
    const requestId = res.headers.get("X-Request-Id");
    let data: unknown;
    try {
      data = await res.json();
    } catch {
      data = null;
    }
    if (!res.ok) {
      const err = (data as ErrorBody | null)?.error;
      throw new ApiError(
        res.status,
        err?.code ?? "internal",
        err?.message ?? `Request failed with status ${res.status}.`,
        err?.request_id ?? requestId,
      );
    }
    return data as T;
  }

  return {
    lookup: (userCode: string) => post<OpsDeviceInfo>("/lookup", { user_code: userCode }),
    decide: (userCode: string, approve: boolean) =>
      post<OpsDeviceDecision>("/approve", { user_code: userCode, approve }),
  };
}

export type OpsDeviceClient = ReturnType<typeof createOpsDeviceClient>;

/** The path of the approval page. `verification_uri` from `/v1/ops/device/start` points here. */
export const OPS_DEVICE_PATH = "/ops/device";

/** The user code from a `?user_code=` query, shown as `XXXX-XXXX`, or "" when there is none. */
export function userCodeFromQuery(search: string): string {
  const raw = new URLSearchParams(search).get("user_code") ?? "";
  return formatUserCode(raw);
}

/** Uppercase, letters only, with a dash after 4 letters. At most 8 letters. */
export function formatUserCode(value: string): string {
  const letters = value
    .toUpperCase()
    .replace(/[^A-Z]/g, "")
    .slice(0, 8);
  return letters.length > 4 ? `${letters.slice(0, 4)}-${letters.slice(4)}` : letters;
}
